<template>
  <section class="chip-input">
    <div
      v-if="disabled"
      class="disable-overlay" />
    <div
      class="chip-input-main"
      :class="chipInputClasses"
      @click="handleComponentClick">
      <label
        v-if="label"
        class="mdc-floating-label mdc-floating-label--float-above">
        {{ label }}
      </label>
      <m-chip-set
        :input="true">
        <m-chip
          v-for="chip in chipset"
          :key="chip.name">
          <m-icon
            v-if="chip.icon && !disabled"
            :slot="chip.icon.slot"
            :icon="chip.icon.icon"
            @click="onIconClick(chip)" />
          {{ chip.name }}
        </m-chip>
        <input
          v-show="componentHasFocus"
          ref="textInput"
          class="text-input"
          type="text"
          @keyup.stop.prevent="checkSubmit"
          @focus="componentHasFocus = true"
          @blur="componentHasFocus = false">
        <div class="bottom-bar" />
      </m-chip-set>
    </div>
    <div class="helper-text">
      {{ helperText }}
    </div>
  </section>
</template>

<script>
import Vue from 'vue'
import Chip from 'material-components-vue/dist/chips'
import Icon from 'material-components-vue/dist/icon'
Vue.use(Icon)
Vue.use(Chip)

export default {
  props: {
    chipset: { required: true, type: [Array] },
    disabled: {
      required: true,
      type: Boolean
    },
    handleChipAdd: { required: true, type: [Function] },
    handleIconClick: { default: () => {}, required: false, type: [Function] },
    helperText: { required: true, type: String },
    label: { default: '', required: false, type: String }
  },
  data () {
    return {
      componentHasFocus: false
    }
  },
  computed: {
    chipInputState () {
      for (let i = 0; i < this.chipset.length; i++) {
        if (this.chipset[i].state === 'error') return 'error'
      }
      return 'valid'
    },
    chipInputClasses () {
      return {
        'chip-input--disabled': this.disabled,
        'chip-input--error': this.error,
        'chip-input--focus': this.componentHasFocus
      }
    }
  },
  methods: {
    checkSubmit (e) {
      if (this.disabled) return
      const ENTER = 13
      const TAB = 9
      const submitted = e.keyCode === ENTER || e.keyCode === TAB

      const text = e.target.value.trim()
      const unique = !(this.chipset.filter((chip) => chip.name === text)).length
      if (submitted && unique) {
        this.handleChipAdd(e.target.value.trim())
        e.target.value = ''
      }
    },
    handleComponentClick () {
      if (this.disabled) return
      this.componentHasFocus = true
      this.$nextTick(() => {
        this.$refs.textInput.focus()
      })
    },
    onIconClick (chip) {
      this.handleIconClick(chip)
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../universal/vars.scss';
  @import "material-components-vue/dist/chips/styles";
  @import "../chips/chips";

  * {
    box-sizing: border-box;
  }
  .chip-input {
    position: relative;
    box-sizing: border-box;
  }
  .chip-input-main {
    min-height: 73px;
    background-color: whitesmoke;
    padding: 24px 8px 0 10px;
    position: relative;
    border-radius: 4px 4px 0 0;
    color: $mdc-theme-black-medium;
  }

  /deep/ .mdc-chip__text {
    color: whitesmoke;
    font-size: 16px;
  }
  .mdc-floating-label {
    margin: -6px 8px 8px 16px;
  }
  .mdc-chip-set--input.mdc-chip-set {
    padding: 8px 4px 15px;
  }
  .mdc-chip-set .mdc-chip {
    height: 24px;
    margin: 0px 0 2px 2px;
  }
  .mdc-chip__icon--trailing {
    margin-right: -9px;
    color: $mdc-theme-white-disabled;
    &:hover {
      color: rgba(255, 255, 255, 0.6);
    }
  }
  .mdc-chip__icon--trailing i:hover {
    color: rgba(255, 255, 255, 0.6);
  }
  .text-input {
    caret-color: $mdc-theme-primary;
    flex: 1;
    margin: 0 0 4px 6px;
    min-height: 20px;
    font-size: 16px;
    font-weight: 300;
    line-height: 12px;
    min-width: 84px;
    background: none;
    border: 0;
    outline: none;
  }
  .bottom-bar {
    width: 100%;
    height: 1px;
    position: absolute;
    left: 0;
    bottom: 1px;
    background-color: $mdc-theme-text-hint-on-background;
  }

  .helper-text {
    margin: 4px 8px 12px 16px;
    font-size: 0.75rem;
    color: $mdc-theme-black-medium;
    visibility: hidden;
  }

  // error
  .error {
    .bottom-bar {
      background-color: $mdc-theme-error;
    }
  }

  // focus
  .chip-input--focus {
    color: $mdc-theme-primary;

    .bottom-bar {
      background-color: $mdc-theme-primary;
    }
  }
  .chip-input--focus ~ .helper-text {
    visibility: visible;
    font-weight: 400;
  }

  // disabled
  .disable-overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 2;
  }
  /deep/ .chip-input--disabled .mdc-chip {
    background-color: rgba(0, 0, 0, 0.12);
  }
  .chip-input--disabled ~ .helper-text {
    visibility: hidden;
  }
  /deep/ .chip-input--disabled .mdc-chip__text {
    color: $black-high-emphasis;
    font-size: 16px;
  }
</style>
